import React, { useEffect, useState } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Modal, 
  Form, 
  Row, 
  Col, 
  Badge, 
  Container, 
  Spinner, 
  Alert,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getFirestore, 
  query, 
  where 
} from 'firebase/firestore';
import firebaseApp from '../../firebase/credenciales';
import { 
  FaPencilAlt, 
  FaTrash, 
  FaPlus, 
  FaClock, 
  FaCalendarAlt, 
  FaBusinessTime,
  FaExclamationTriangle,
  FaInfoCircle,
  FaCheck,
  FaUsers,
  FaChartBar
} from 'react-icons/fa';
import { BsClockHistory, BsClock, BsCalendar3 } from 'react-icons/bs';
import Swal from 'sweetalert2';

const db = getFirestore(firebaseApp);

const diasSemana = [
    { value: 1, label: 'Lunes', short: 'L' },
    { value: 2, label: 'Martes', short: 'M' },
    { value: 3, label: 'Miércoles', short: 'X' },
    { value: 4, label: 'Jueves', short: 'J' },
    { value: 5, label: 'Viernes', short: 'V' },
    { value: 6, label: 'Sábado', short: 'S' },
    { value: 7, label: 'Domingo', short: 'D' }
];

const HORARIO_BASE = {
    hora_inicio: "08:00",
    hora_fin: "17:00",
    hora_refrigerio_inicio: "13:00",
    hora_refrigerio_fin: "14:00",
};

function construirHorariosPorDia(diasLaborables = [], base = {}) {
    const hora_inicio = base.hora_inicio || HORARIO_BASE.hora_inicio;
    const hora_fin = base.hora_fin || HORARIO_BASE.hora_fin;
    const hora_refrigerio_inicio = base.hora_refrigerio_inicio || HORARIO_BASE.hora_refrigerio_inicio;
    const hora_refrigerio_fin = base.hora_refrigerio_fin || HORARIO_BASE.hora_refrigerio_fin;
    const horarios = { ...(base.horarios_por_dia || {}) };

    diasLaborables.forEach((dia) => {
        if (!horarios[dia]) {
            horarios[dia] = {
                hora_inicio,
                hora_fin,
                hora_refrigerio_inicio,
                hora_refrigerio_fin,
            };
        }
    });
    return horarios;
}

function calcularHorasDia(horarioDia = {}) {
    const hj = calcularHorasEntre(horarioDia.hora_inicio, horarioDia.hora_fin);
    const hr = calcularHorasEntre(horarioDia.hora_refrigerio_inicio, horarioDia.hora_refrigerio_fin);
    return Math.max(0, hj - hr);
}

// Función para calcular la diferencia en horas entre dos horas tipo "HH:mm"
function calcularHorasEntre(horaInicio, horaFin) {
    if (!horaInicio || !horaFin) return 0;
    const [h1, m1] = horaInicio.split(':').map(Number);
    const [h2, m2] = horaFin.split(':').map(Number);
    let minutos = (h2 * 60 + m2) - (h1 * 60 + m1);
    if (minutos < 0) minutos += 24 * 60; // Soporta turnos nocturnos
    return minutos / 60;
}

export const calcularHorasSemanales = (regimenTrabajo) => {
  try {
    // Validaciones
    if (!regimenTrabajo || !regimenTrabajo.tipo) {
      console.warn("calcularHorasSemanales: régimen de trabajo inválido");
      return 48; // Horas estándar
    }

    const { tipo, horas_por_dia, dias_por_semana } = regimenTrabajo;

    switch (tipo) {
      case "21x10": // 21 días trabajados, 10 de descanso
        // Cálculo promedio semanal: (21 días * horas_por_dia) / 31 días ≈ horas semanales
        const horasMensuales = 21 * (horas_por_dia || 8);
        return Math.round(horasMensuales / 4.3); // Promedio semanal

      case "15x7": // 15 días trabajados, 7 de descanso
        const horasMensuales15x7 = 15 * (horas_por_dia || 8);
        return Math.round(horasMensuales15x7 / 4.3);

      case "ESTANDAR":
      default:
        return (dias_por_semana || 6) * (horas_por_dia || 8);
    }
  } catch (error) {
    console.error("Error en calcularHorasSemanales:", error);
    return 48;
  }
};

const RegimenTrabajo = ({ usuario }) => {
    const ruc = usuario?.ruc || '';
    const [regimenes, setRegimenes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [show, setShow] = useState(false);
    const [editRegimen, setEditRegimen] = useState(null);
    const [form, setForm] = useState({
        nombre: '',
        descripcion: '',
        dias_laborables: [],
        horas_por_dia: 8,
        hora_inicio: '08:00',
        hora_fin: '17:00',
        tolerancia_minutos: 10,
        considera_nocturno: false,
        ruc: ruc,
        dias_trabajo: 0,
        dias_descanso: 0,
        hora_refrigerio_inicio: '13:00',
        hora_refrigerio_fin: '14:00',
        horarios_por_dia: {},
    });

    // Mantener el ruc actualizado en el form si cambia el usuario
    useEffect(() => {
        setForm(f => ({ ...f, ruc: ruc }));
    }, [ruc]);

    const fetchRegimenes = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, 'pl_regimen_trabajo'), where('ruc', '==', ruc));
            const snap = await getDocs(q);
            const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setRegimenes(data);
        } catch (error) {
            console.error('Error al cargar regímenes:', error);
            mostrarError('Error al cargar los regímenes de trabajo');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { 
        if (ruc) fetchRegimenes(); 
    }, [ruc]);

    const mostrarError = (mensaje) => {
        Swal.fire({
            icon: 'error',
            title: 'Error',
            text: mensaje,
            confirmButtonColor: '#8B4513'
        });
    };

    const mostrarExito = (mensaje) => {
        Swal.fire({
            icon: 'success',
            title: 'Éxito',
            text: mensaje,
            confirmButtonColor: '#8B4513',
            timer: 2000,
            showConfirmButton: false
        });
    };

    const handleShow = (regimen = null) => {
        setEditRegimen(regimen);
        const baseForm = regimen ? { ...regimen, ruc } : {
            nombre: '',
            descripcion: '',
            dias_laborables: [],
            horas_por_dia: 8,
            hora_inicio: '08:00',
            hora_fin: '17:00',
            tolerancia_minutos: 10,
            considera_nocturno: false,
            ruc,
            dias_trabajo: 0,
            dias_descanso: 0,
            hora_refrigerio_inicio: '13:00',
            hora_refrigerio_fin: '14:00',
            horarios_por_dia: {},
        };
        setForm({
            ...baseForm,
            horarios_por_dia: construirHorariosPorDia(baseForm.dias_laborables || [], baseForm),
        });
        setShow(true);
    };

    const handleSave = async () => {
        if (!form.nombre.trim() || !ruc) {
            mostrarError('El nombre del régimen es obligatorio');
            return;
        }

        if (form.dias_laborables.length === 0 && form.dias_trabajo === 0) {
            mostrarError('Debe especificar días laborables o configurar un ciclo de trabajo/descanso');
            return;
        }

        setSaving(true);
        try {
            if (editRegimen) {
                await updateDoc(doc(db, 'pl_regimen_trabajo', editRegimen.id), { ...form, ruc });
                mostrarExito('Régimen actualizado correctamente');
            } else {
                await addDoc(collection(db, 'pl_regimen_trabajo'), { ...form, ruc });
                mostrarExito('Régimen creado correctamente');
            }
            setShow(false);
            await fetchRegimenes();
        } catch (error) {
            console.error('Error al guardar:', error);
            mostrarError('Error al guardar el régimen de trabajo');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id, nombre) => {
        const result = await Swal.fire({
            title: '¿Eliminar régimen?',
            text: `Se eliminará permanentemente el régimen "${nombre}"`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#8B4513',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            setLoading(true);
            try {
                await deleteDoc(doc(db, 'pl_regimen_trabajo', id));
                await fetchRegimenes();
                mostrarExito('Régimen eliminado correctamente');
            } catch (error) {
                console.error('Error al eliminar:', error);
                mostrarError('Error al eliminar el régimen');
            } finally {
                setLoading(false);
            }
        }
    };

    // Calcular horas por día a partir de hora inicio, hora fin y refrigerio
    const horariosPorDia = construirHorariosPorDia(form.dias_laborables, form);
    const horasTotalesDiasSeleccionados = form.dias_laborables.reduce((sum, dia) => {
        return sum + calcularHorasDia(horariosPorDia[dia]);
    }, 0);
    const horasPorDia = form.dias_laborables.length > 0
        ? horasTotalesDiasSeleccionados / form.dias_laborables.length
        : calcularHorasDia(form);

    // Calcular total de horas semanales considerando ciclo trabajo/descanso si aplica
    let totalHorasSemanales = 0;
    if (form.dias_trabajo > 0 && form.dias_descanso > 0) {
        const horasCiclo = form.dias_trabajo * horasPorDia;
        const semanasCiclo = (form.dias_trabajo + form.dias_descanso) / 7;
        totalHorasSemanales = semanasCiclo > 0 ? (horasCiclo / semanasCiclo) : 0;
    } else {
        totalHorasSemanales = horasTotalesDiasSeleccionados;
    }
    const excedeLimite = totalHorasSemanales > 48;

    // Calcular estadísticas
    const calcularEstadisticas = () => {
        const totalRegimenes = regimenes.length;
        const regimenesTournoContinuo = regimenes.filter(r => 
            r.dias_trabajo > 0 && r.dias_descanso > 0
        ).length;
        const regimenesNocturnos = regimenes.filter(r => r.considera_nocturno).length;
        const promedioHorasDia = totalRegimenes > 0 
            ? (regimenes.reduce((sum, r) => {
                const hj = calcularHorasEntre(r.hora_inicio, r.hora_fin);
                const hr = calcularHorasEntre(r.hora_refrigerio_inicio, r.hora_refrigerio_fin);
                return sum + Math.max(0, hj - hr);
            }, 0) / totalRegimenes).toFixed(1)
            : 0;

        return {
            totalRegimenes,
            regimenesTournoContinuo,
            regimenesNocturnos,
            promedioHorasDia
        };
    };

    const estadisticas = calcularEstadisticas();

    if (loading) {
        return (
            <div
                className="min-vh-100 d-flex justify-content-center align-items-center"
                style={{
                    background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
                }}
            >
                <div className="text-center">
                    <Spinner
                        animation="border"
                        style={{ color: "#8B4513", width: "3rem", height: "3rem" }}
                    />
                    <p className="mt-3 mb-0" style={{ color: "#8B4513" }}>
                        Cargando regímenes de trabajo...
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div
            className="min-vh-100"
            style={{
                background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
                minHeight: "100vh",
                width: "100vw",
                maxWidth: "100%",
                padding: "20px 0",
            }}
        >
            <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
                {/* Marco principal */}
                <Card
                    className="shadow-lg border-0"
                    style={{
                        borderRadius: "15px",
                        overflow: "hidden",
                        minHeight: "90vh",
                    }}
                >
                    {/* Header Premium */}
                    <Card.Header
                        className="text-white py-4"
                        style={{
                            background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                            borderBottom: "3px solid #D2691E",
                        }}
                    >
                        <div className="text-center">
                            <h2 className="fw-bold mb-1">
                                <FaBusinessTime className="me-3" />
                                Regímenes de Trabajo
                            </h2>
                            <p className="mb-0 opacity-75">
                                Configure horarios, jornadas y turnos de trabajo
                            </p>
                        </div>
                    </Card.Header>

                    <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
                        {/* Barra superior con información */}
                        <Card
                            className="shadow-sm border-0 mb-4"
                            style={{ borderRadius: "15px" }}
                        >
                            <Card.Body className="p-4">
                                <Row className="align-items-center">
                                    <Col md={1}>
                                        <div
                                            className="rounded-3 p-3 text-center"
                                            style={{
                                                background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                                                color: "white",
                                            }}
                                        >
                                            <FaCalendarAlt size={24} />
                                        </div>
                                    </Col>
                                    <Col md={7}>
                                        <h5 className="mb-1 fw-semibold">
                                            Gestión de Regímenes Laborales
                                        </h5>
                                        <p className="text-muted mb-0 small">
                                            Defina horarios, jornadas y configuraciones especiales como turnos rotativos
                                        </p>
                                    </Col>
                                    <Col md={4} className="text-end">
                                        <Button
                                            onClick={() => handleShow()}
                                            size="lg"
                                            className="px-4 py-2"
                                            style={{
                                                borderRadius: "15px",
                                                background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                                                border: "none",
                                                fontWeight: "600"
                                            }}
                                        >
                                            <FaPlus className="me-2" />
                                            Nuevo Régimen
                                        </Button>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Estadísticas */}
                        <Row className="mb-4">
                            <Col md={3}>
                                <Card
                                    className="border-0 shadow-sm"
                                    style={{ borderRadius: "15px" }}
                                >
                                    <Card.Body
                                        className="text-center"
                                        style={{
                                            background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                                            color: "white",
                                            borderRadius: "15px",
                                        }}
                                    >
                                        <FaUsers size={32} className="mb-2" />
                                        <h4 className="fw-bold">{estadisticas.totalRegimenes}</h4>
                                        <p className="mb-0">Total Regímenes</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3}>
                                <Card
                                    className="border-0 shadow-sm"
                                    style={{ borderRadius: "15px" }}
                                >
                                    <Card.Body
                                        className="text-center"
                                        style={{
                                            background: "linear-gradient(135deg, #007bff 0%, #0056b3 100%)",
                                            color: "white",
                                            borderRadius: "15px",
                                        }}
                                    >
                                        <BsCalendar3 size={32} className="mb-2" />
                                        <h4 className="fw-bold">{estadisticas.regimenesTournoContinuo}</h4>
                                        <p className="mb-0">Turnos Continuos</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3}>
                                <Card
                                    className="border-0 shadow-sm"
                                    style={{ borderRadius: "15px" }}
                                >
                                    <Card.Body
                                        className="text-center"
                                        style={{
                                            background: "linear-gradient(135deg, #6f42c1 0%, #563d7c 100%)",
                                            color: "white",
                                            borderRadius: "15px",
                                        }}
                                    >
                                        <BsClockHistory size={32} className="mb-2" />
                                        <h4 className="fw-bold">{estadisticas.regimenesNocturnos}</h4>
                                        <p className="mb-0">Turnos Nocturnos</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3}>
                                <Card
                                    className="border-0 shadow-sm"
                                    style={{ borderRadius: "15px" }}
                                >
                                    <Card.Body
                                        className="text-center"
                                        style={{
                                            background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                                            color: "white",
                                            borderRadius: "15px",
                                        }}
                                    >
                                        <FaChartBar size={32} className="mb-2" />
                                        <h4 className="fw-bold">{estadisticas.promedioHorasDia}</h4>
                                        <p className="mb-0">Promedio Hrs/Día</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Tabla de regímenes */}
                        <Card className="shadow-sm border-0" style={{ borderRadius: "15px" }}>
                            <div style={{ borderRadius: "15px", overflow: "hidden" }}>
                                {regimenes.length === 0 ? (
                                    <Card.Body className="text-center py-5">
                                        <div className="text-muted">
                                            <FaCalendarAlt size={48} className="mb-3 opacity-50" />
                                            <h5>No hay regímenes registrados</h5>
                                            <p>Cree el primer régimen de trabajo para comenzar</p>
                                            <Button
                                                onClick={() => handleShow()}
                                                variant="outline-primary"
                                                className="mt-2"
                                                style={{
                                                    borderColor: "#8B4513",
                                                    color: "#8B4513",
                                                    borderRadius: "10px"
                                                }}
                                            >
                                                <FaPlus className="me-2" />
                                                Crear primer régimen
                                            </Button>
                                        </div>
                                    </Card.Body>
                                ) : (
                                    <Table responsive hover className="mb-0" style={{ fontSize: "0.9rem" }}>
                                        <thead
                                            style={{
                                                backgroundColor: "#f8f9fa",
                                            }}
                                        >
                                            <tr>
                                                <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                                                    <FaBusinessTime className="me-1" />
                                                    Régimen
                                                </th>
                                                <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                                                    <BsCalendar3 className="me-1" />
                                                    Días
                                                </th>
                                                <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                                                    <FaClock className="me-1" />
                                                    Hrs/Día
                                                </th>
                                                <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                                                    <BsClock className="me-1" />
                                                    Horario
                                                </th>
                                                <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                                                    Refrigerio
                                                </th>
                                                <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                                                    Tolerancia
                                                </th>
                                                <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                                                    Ciclo
                                                </th>
                                                <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                                                    Nocturno
                                                </th>
                                                <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center">
                                                    Acciones
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {regimenes.map(r => {
                                                const horasCalculadas = (() => {
                                                    const horarios = construirHorariosPorDia(r.dias_laborables || [], r);
                                                    if ((r.dias_laborables || []).length > 0) {
                                                        const total = (r.dias_laborables || []).reduce((sum, dia) => sum + calcularHorasDia(horarios[dia]), 0);
                                                        return total / (r.dias_laborables || []).length;
                                                    }
                                                    return calcularHorasDia(r);
                                                })();

                                                return (
                                                    <tr key={r.id} style={{ borderBottom: "1px solid #f1f3f4" }}>
                                                        <td className="py-3 px-3">
                                                            <div>
                                                                <div className="fw-bold text-dark">{r.nombre}</div>
                                                                {r.descripcion && (
                                                                    <div className="text-muted small mt-1">{r.descripcion}</div>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="py-3 px-3">
                                                            <div className="d-flex flex-wrap gap-1">
                                                                {r.dias_laborables?.sort().map(d => (
                                                                    <Badge 
                                                                        key={d}
                                                                        style={{
                                                                            background: "linear-gradient(135deg, #007bff 0%, #0056b3 100%)",
                                                                            fontSize: "0.7rem",
                                                                            padding: "4px 8px"
                                                                        }}
                                                                    >
                                                                        {diasSemana.find(ds => ds.value === d)?.short}
                                                                    </Badge>
                                                                ))}
                                                            </div>
                                                        </td>
                                                        <td className="py-3 px-3">
                                                            <Badge
                                                                style={{
                                                                    background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                                                                    fontSize: "0.8rem",
                                                                    padding: "6px 10px"
                                                                }}
                                                            >
                                                                {horasCalculadas.toFixed(1)}h
                                                            </Badge>
                                                        </td>
                                                        <td className="py-3 px-3">
                                                            <div className="small text-muted">
                                                                {Object.keys(r.horarios_por_dia || {}).length > 0 ? "Variable por día" : `${r.hora_inicio} - ${r.hora_fin}`}
                                                            </div>
                                                        </td>
                                                        <td className="py-3 px-3">
                                                            <div className="small text-muted">
                                                                {r.hora_refrigerio_inicio && r.hora_refrigerio_fin 
                                                                    ? `${r.hora_refrigerio_inicio} - ${r.hora_refrigerio_fin}`
                                                                    : '-'
                                                                }
                                                            </div>
                                                        </td>
                                                        <td className="py-3 px-3">
                                                            <Badge
                                                                bg="secondary"
                                                                style={{ fontSize: "0.7rem" }}
                                                            >
                                                                {r.tolerancia_minutos} min
                                                            </Badge>
                                                        </td>
                                                        <td className="py-3 px-3">
                                                            {r.dias_trabajo > 0 && r.dias_descanso > 0 ? (
                                                                <Badge
                                                                    style={{
                                                                        background: "linear-gradient(135deg, #fd7e14 0%, #e55a4e 100%)",
                                                                        fontSize: "0.7rem"
                                                                    }}
                                                                >
                                                                    {r.dias_trabajo}x{r.dias_descanso}
                                                                </Badge>
                                                            ) : (
                                                                <span className="text-muted">-</span>
                                                            )}
                                                        </td>
                                                        <td className="py-3 px-3">
                                                            <Badge
                                                                bg={r.considera_nocturno ? "warning" : "light"}
                                                                text={r.considera_nocturno ? "dark" : "muted"}
                                                                style={{ fontSize: "0.7rem" }}
                                                            >
                                                                {r.considera_nocturno ? "Sí" : "No"}
                                                            </Badge>
                                                        </td>
                                                        <td className="py-3 px-3 text-center">
                                                            <div className="d-flex gap-1 justify-content-center">
                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={<Tooltip>Editar régimen</Tooltip>}
                                                                >
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline-primary"
                                                                        onClick={() => handleShow(r)}
                                                                        style={{ 
                                                                            borderRadius: "8px",
                                                                            borderColor: "#8B4513",
                                                                            color: "#8B4513"
                                                                        }}
                                                                    >
                                                                        <FaPencilAlt size={12} />
                                                                    </Button>
                                                                </OverlayTrigger>
                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={<Tooltip>Eliminar régimen</Tooltip>}
                                                                >
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline-danger"
                                                                        onClick={() => handleDelete(r.id, r.nombre)}
                                                                        style={{ borderRadius: "8px" }}
                                                                    >
                                                                        <FaTrash size={12} />
                                                                    </Button>
                                                                </OverlayTrigger>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                )}
                            </div>
                        </Card>
                    </Card.Body>
                </Card>

                {/* Modal de régimen */}
                <Modal show={show} onHide={() => setShow(false)} size="lg" centered>
                    <Modal.Header
                        closeButton
                        className="text-white"
                        style={{
                            background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                        }}
                    >
                        <Modal.Title className="fw-bold">
                            <FaBusinessTime className="me-2" />
                            {editRegimen ? 'Editar' : 'Nuevo'} Régimen de Trabajo
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ backgroundColor: "#f8f9fa" }}>
                        <Form>
                            <Row>
                                <Col md={8}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                                            <FaBusinessTime className="me-1" />
                                            Nombre del Régimen
                                        </Form.Label>
                                        <Form.Control
                                            value={form.nombre}
                                            onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                                            maxLength={40}
                                            required
                                            style={{ borderRadius: "10px" }}
                                            placeholder="Ej: Horario Administrativo"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                                            <FaClock className="me-1" />
                                            Horas por Día
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={horasPorDia.toFixed(2)}
                                            readOnly
                                            style={{ 
                                                borderRadius: "10px", 
                                                backgroundColor: "#e9ecef",
                                                fontWeight: "600"
                                            }}
                                        />
                                        <Form.Text className="text-muted">
                                            Calculado automáticamente
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                                    <FaInfoCircle className="me-1" />
                                    Descripción
                                </Form.Label>
                                <Form.Control
                                    value={form.descripcion}
                                    onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
                                    as="textarea"
                                    rows={2}
                                    maxLength={120}
                                    style={{ borderRadius: "10px" }}
                                    placeholder="Descripción del régimen de trabajo"
                                />
                            </Form.Group>

                            <Form.Group className="mb-4">
                                <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                                    <BsCalendar3 className="me-1" />
                                    Días Laborables
                                </Form.Label>
                                <div className="d-flex flex-wrap gap-2 mt-2">
                                    {diasSemana.map(d => (
                                        <Form.Check
                                            key={d.value}
                                            type="checkbox"
                                            id={`dia-${d.value}`}
                                            label={d.label}
                                            checked={form.dias_laborables.includes(d.value)}
                                            onChange={e => {
                                                const checked = e.target.checked;
                                                setForm(f => ({
                                                    ...f,
                                                    dias_laborables: checked
                                                        ? [...f.dias_laborables, d.value]
                                                        : f.dias_laborables.filter(val => val !== d.value),
                                                    horarios_por_dia: checked
                                                        ? {
                                                            ...f.horarios_por_dia,
                                                            [d.value]: f.horarios_por_dia?.[d.value] || {
                                                                hora_inicio: f.hora_inicio,
                                                                hora_fin: f.hora_fin,
                                                                hora_refrigerio_inicio: f.hora_refrigerio_inicio,
                                                                hora_refrigerio_fin: f.hora_refrigerio_fin,
                                                            },
                                                        }
                                                        : (() => {
                                                            const copia = { ...(f.horarios_por_dia || {}) };
                                                            delete copia[d.value];
                                                            return copia;
                                                        })()
                                                }));
                                            }}
                                            className="user-select-none"
                                            style={{
                                                padding: "8px 12px",
                                                backgroundColor: form.dias_laborables.includes(d.value) ? "#e3f2fd" : "#f8f9fa",
                                                borderRadius: "8px",
                                                border: "1px solid #dee2e6"
                                            }}
                                        />
                                    ))}
                                </div>
                            </Form.Group>

                            <Card className="mb-3 border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                                <Card.Header
                                    className="py-2"
                                    style={{
                                        background: "linear-gradient(135deg, #D2691E 0%, #CD853F 100%)",
                                        color: "white",
                                        borderRadius: "15px 15px 0 0"
                                    }}
                                >
                                    <h6 className="mb-0 fw-semibold">
                                        <BsClock className="me-2" />
                                        Horario de Trabajo
                                    </h6>
                                </Card.Header>
                                <Card.Body className="p-3">
                                    <Alert variant="light" className="border">
                                        Horario base del régimen. Si activas horarios por día, este bloque se usa como plantilla.
                                    </Alert>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                                                    Hora de Inicio
                                                </Form.Label>
                                                <Form.Control
                                                    type="time"
                                                    value={form.hora_inicio}
                                                    onChange={e => setForm(f => ({ ...f, hora_inicio: e.target.value }))}
                                                    style={{ borderRadius: "10px" }}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                                                    Hora de Fin
                                                </Form.Label>
                                                <Form.Control
                                                    type="time"
                                                    value={form.hora_fin}
                                                    onChange={e => setForm(f => ({ ...f, hora_fin: e.target.value }))}
                                                    style={{ borderRadius: "10px" }}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                                                    Refrigerio - Inicio
                                                </Form.Label>
                                                <Form.Control
                                                    type="time"
                                                    value={form.hora_refrigerio_inicio}
                                                    onChange={e => setForm(f => ({ ...f, hora_refrigerio_inicio: e.target.value }))}
                                                    style={{ borderRadius: "10px" }}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                                                    Refrigerio - Fin
                                                </Form.Label>
                                                <Form.Control
                                                    type="time"
                                                    value={form.hora_refrigerio_fin}
                                                    onChange={e => setForm(f => ({ ...f, hora_refrigerio_fin: e.target.value }))}
                                                    style={{ borderRadius: "10px" }}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                            <Card className="mb-3 border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                                <Card.Header
                                    className="py-2 d-flex justify-content-between align-items-center"
                                    style={{
                                        background: "linear-gradient(135deg, #0d6efd 0%, #0b5ed7 100%)",
                                        color: "white",
                                        borderRadius: "15px 15px 0 0"
                                    }}
                                >
                                    <h6 className="mb-0 fw-semibold">Horario por Día (opcional)</h6>
                                    <Button
                                        size="sm"
                                        variant="light"
                                        onClick={() =>
                                            setForm((f) => ({
                                                ...f,
                                                horarios_por_dia: construirHorariosPorDia(f.dias_laborables, f),
                                            }))
                                        }
                                    >
                                        Rellenar desde base
                                    </Button>
                                </Card.Header>
                                <Card.Body className="p-3">
                                    {form.dias_laborables.length === 0 && (
                                        <Alert variant="warning" className="mb-0">
                                            Selecciona días laborables para configurar horarios específicos.
                                        </Alert>
                                    )}
                                    {[...form.dias_laborables].sort((a, b) => a - b).map((dia) => {
                                        const h = horariosPorDia[dia] || {};
                                        return (
                                            <div key={`horario-dia-${dia}`} className="border rounded p-2 mb-2">
                                                <div className="fw-semibold mb-2">{diasSemana.find(ds => ds.value === dia)?.label}</div>
                                                <Row>
                                                    <Col md={3}>
                                                        <Form.Control
                                                            type="time"
                                                            value={h.hora_inicio || ""}
                                                            onChange={(e) => setForm((f) => ({
                                                                ...f,
                                                                horarios_por_dia: {
                                                                    ...(f.horarios_por_dia || {}),
                                                                    [dia]: { ...(f.horarios_por_dia?.[dia] || {}), hora_inicio: e.target.value },
                                                                },
                                                            }))}
                                                        />
                                                    </Col>
                                                    <Col md={3}>
                                                        <Form.Control
                                                            type="time"
                                                            value={h.hora_fin || ""}
                                                            onChange={(e) => setForm((f) => ({
                                                                ...f,
                                                                horarios_por_dia: {
                                                                    ...(f.horarios_por_dia || {}),
                                                                    [dia]: { ...(f.horarios_por_dia?.[dia] || {}), hora_fin: e.target.value },
                                                                },
                                                            }))}
                                                        />
                                                    </Col>
                                                    <Col md={3}>
                                                        <Form.Control
                                                            type="time"
                                                            value={h.hora_refrigerio_inicio || ""}
                                                            onChange={(e) => setForm((f) => ({
                                                                ...f,
                                                                horarios_por_dia: {
                                                                    ...(f.horarios_por_dia || {}),
                                                                    [dia]: { ...(f.horarios_por_dia?.[dia] || {}), hora_refrigerio_inicio: e.target.value },
                                                                },
                                                            }))}
                                                        />
                                                    </Col>
                                                    <Col md={3}>
                                                        <Form.Control
                                                            type="time"
                                                            value={h.hora_refrigerio_fin || ""}
                                                            onChange={(e) => setForm((f) => ({
                                                                ...f,
                                                                horarios_por_dia: {
                                                                    ...(f.horarios_por_dia || {}),
                                                                    [dia]: { ...(f.horarios_por_dia?.[dia] || {}), hora_refrigerio_fin: e.target.value },
                                                                },
                                                            }))}
                                                        />
                                                    </Col>
                                                </Row>
                                            </div>
                                        );
                                    })}
                                </Card.Body>
                            </Card>

                            {/* Alertas de horas semanales */}
                            <Card
                                className="mb-3 border-0"
                                style={{
                                    borderRadius: "15px",
                                    backgroundColor: excedeLimite ? "#fff3cd" : "#d1ecf1"
                                }}
                            >
                                <Card.Body className="p-3">
                                    <div className="d-flex align-items-center">
                                        {excedeLimite ? (
                                            <FaExclamationTriangle className="text-warning me-2" size={20} />
                                        ) : (
                                            <FaCheck className="text-info me-2" size={20} />
                                        )}
                                        <div>
                                            <strong>
                                                Total de horas semanales: {totalHorasSemanales.toFixed(2)} h
                                            </strong>
                                            {excedeLimite && (
                                                <div className="text-warning mt-1 small">
                                                    <strong>¡Atención!</strong> Excede el límite legal de 48 horas semanales.
                                                    Las horas adicionales deben pagarse como horas extras.
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>

                            <Row>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                                            <BsClockHistory className="me-1" />
                                            Tolerancia (min)
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            min={0}
                                            max={60}
                                            value={form.tolerancia_minutos}
                                            onChange={e => setForm(f => ({ ...f, tolerancia_minutos: parseInt(e.target.value) }))}
                                            style={{ borderRadius: "10px" }}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                                            Días de Trabajo
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            min={0}
                                            max={31}
                                            value={form.dias_trabajo}
                                            onChange={e => setForm(f => ({ ...f, dias_trabajo: parseInt(e.target.value) || 0 }))}
                                            style={{ borderRadius: "10px" }}
                                            placeholder="Ej: 21"
                                        />
                                        <Form.Text className="text-muted">
                                            Para turnos rotativos
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                                            Días de Descanso
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            min={0}
                                            max={31}
                                            value={form.dias_descanso}
                                            onChange={e => setForm(f => ({ ...f, dias_descanso: parseInt(e.target.value) || 0 }))}
                                            style={{ borderRadius: "10px" }}
                                            placeholder="Ej: 10"
                                        />
                                        <Form.Text className="text-muted">
                                            Para turnos rotativos
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="d-flex align-items-center">
                                        <Form.Check
                                            type="checkbox"
                                            id="nocturno-check"
                                            label="Considera trabajo nocturno"
                                            checked={form.considera_nocturno}
                                            onChange={e => setForm(f => ({ ...f, considera_nocturno: e.target.checked }))}
                                            className="fw-semibold"
                                            style={{ color: "#8B4513" }}
                                        />
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={
                                                <Tooltip>
                                                    El trabajo nocturno tiene sobretasa del 35%
                                                </Tooltip>
                                            }
                                        >
                                            <FaInfoCircle className="ms-2 text-muted" style={{ cursor: "help" }} />
                                        </OverlayTrigger>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer style={{ backgroundColor: "#f8f9fa" }}>
                        <Button
                            variant="secondary"
                            onClick={() => setShow(false)}
                            style={{ borderRadius: "10px" }}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleSave}
                            disabled={saving}
                            style={{
                                borderRadius: "10px",
                                background: saving 
                                    ? "linear-gradient(135deg, #6c757d 0%, #495057 100%)"
                                    : "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                                border: "none"
                            }}
                        >
                            {saving ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Guardando...
                                </>
                            ) : (
                                <>
                                    <FaCheck className="me-2" />
                                    Guardar
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Container>
        </div>
    );
};

export default RegimenTrabajo;
